/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */

export interface DemoUser {
  userId: string;
  email: string;
  subscriptionTier: 'pro';
  isDemoUser: true;  // Renamed from isDemo for clarity (user-level flag)
  isDemo?: true;     // Legacy property for backward compatibility
  demoLabel: string;
  demoExpiresAt: string;
  sessionId: string;
}

export interface DemoValidationResult {
  success: boolean;
  user?: DemoUser;
  error?: string;
}

/**
 * Extracts demo token from URL parameters
 */
export function getDemoTokenFromUrl(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('token') || urlParams.get('demo');
}

/**
 * Validates a demo token with the backend API
 */
export async function validateDemoToken(token: string): Promise<DemoValidationResult> {
  try {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://localhost:7095';
    const response = await fetch(`${API_BASE_URL}/api/demo/validate?token=${encodeURIComponent(token)}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Invalid or expired token' }));
      return {
        success: false,
        error: errorData.error || `Validation failed: ${response.status}`
      };
    }

    const user = await response.json();

    // Store in sessionStorage for page refreshes
    sessionStorage.setItem('demo_user', JSON.stringify(user));
    sessionStorage.setItem('demo_token', token);

    return {
      success: true,
      user
    };
  } catch (error) {
    console.error('Demo token validation error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error'
    };
  }
}

/**
 * Gets demo user from sessionStorage (for page refreshes)
 */
export function getDemoUserFromStorage(): DemoUser | null {
  try {
    const stored = sessionStorage.getItem('demo_user');
    if (!stored) return null;

    const user = JSON.parse(stored);

    // Validate expiration
    if (user.demoExpiresAt && new Date(user.demoExpiresAt) < new Date()) {
      clearDemoSession();
      return null;
    }

    return user;
  } catch (error) {
    console.error('Error reading demo user from storage:', error);
    return null;
  }
}

/**
 * Clears demo session data
 */
export function clearDemoSession(): void {
  sessionStorage.removeItem('demo_user');
  sessionStorage.removeItem('demo_token');
}

/**
 * Checks if currently in demo mode
 */
export function isDemoMode(): boolean {
  return getDemoUserFromStorage() !== null;
}

/**
 * Initializes demo mode if token is present in URL
 * Returns true if demo mode was activated
 */
export async function initializeDemoMode(): Promise<DemoValidationResult> {
  // Check if already in demo mode from storage
  const existingUser = getDemoUserFromStorage();
  if (existingUser) {
    return { success: true, user: existingUser };
  }

  // Check for token in URL
  const token = getDemoTokenFromUrl();
  if (!token) {
    return { success: false, error: 'No demo token found' };
  }

  // Validate token
  return await validateDemoToken(token);
}
